import React, { useMemo } from 'react';
import { ChevronLeft, SlidersHorizontal } from 'lucide-react';
import FilterSelect from '../Campaigns/FilterSelect';
import DateRangePicker from '../Filters/DateRangePicker';
import useFilterOptions from '../../hooks/useFilterOptions';
import { buildHierarchy, groupsFor, unitsFor } from './filterOptions';

/**
 * Right-hand collapsible filter sidebar shared by all Quality KPI tabs —
 * Product Domain / Group / Unit (cross-filtered) + Date Range.
 *
 * Automation Coverage doesn't use Date Range (confirmed when its backend
 * stories were scoped), so `hideDateRange` disables that control rather than
 * removing it, keeping the sidebar's shape consistent across tabs.
 */
function FiltersSidebar({ filters, onFiltersChange, dateRange, onDateRangeChange, hideDateRange, collapsed, onToggleCollapsed }) {
  // Fetch filter options from API, refetching when dateRange changes
  const { data: filterOptionsData, loading: filterOptionsLoading } = useFilterOptions(dateRange);

  // Build hierarchy from API response, with fallback to empty structure while loading
  const hierarchy = useMemo(() => {
    return buildHierarchy(filterOptionsData || { domains: [] });
  }, [filterOptionsData]);

  // Get available options based on selections and current hierarchy
  const domainOptions = useMemo(
    () => {
      const domains = filterOptionsData?.domains?.map(d => d.name) || [];
      return domains.map(d => ({ value: d, count: null }));
    },
    [filterOptionsData]
  );

  const groupOptions = useMemo(
    () => groupsFor(filters.domain, hierarchy).map(g => ({ value: g, count: null })),
    [filters.domain, hierarchy]
  );

  const unitOptions = useMemo(
    () => unitsFor(filters.group, hierarchy).map(u => ({ value: u, count: null })),
    [filters.group, hierarchy]
  );

  const set = (key) => (value) => onFiltersChange({ ...filters, [key]: value });

  return (
    <div className={`qk-sidebar-container${collapsed ? ' collapsed' : ''}`}>
      <button
        className={`sidebar-toggle-button right${collapsed ? ' collapsed' : ''}`}
        onClick={onToggleCollapsed}
        aria-label="Toggle filters"
      >
        <ChevronLeft size={20} />
      </button>
      <div className="qk-sidebar">
        <span className="campaign-sidebar-collapsed-label"><SlidersHorizontal size={20} /></span>
        <div className="sidebar-filters-content">
          <div className="sidebar-title"><SlidersHorizontal size={16} style={{ marginRight: 5, verticalAlign: 'middle' }} />Filters</div>

          <div className="filter-item">
            <label>Product Domain</label>
            <FilterSelect options={domainOptions} value={filters.domain} onChange={set('domain')} disabled={filterOptionsLoading} />
          </div>
          <div className="filter-item">
            <label>Product Group</label>
            <FilterSelect options={groupOptions} value={filters.group} onChange={set('group')} disabled={filterOptionsLoading} />
          </div>
          <div className="filter-item">
            <label>Product Unit</label>
            <FilterSelect options={unitOptions} value={filters.unit} onChange={set('unit')} disabled={filterOptionsLoading} />
          </div>
          <div className={`filter-item${hideDateRange ? ' filter-item--disabled' : ''}`}>
            <label>Date Range{hideDateRange && <span className="qk-filter-note"> (not used on this tab)</span>}</label>
            <DateRangePicker
              value={dateRange}
              preset={dateRange.preset}
              onChange={onDateRangeChange}
              enabledPresets={['7d', '30d', '90d', 'lastMonth', '6m', '12m', 'qtd', 'ytd']}
              mode="dialog"
              disabled={hideDateRange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default FiltersSidebar;
